import React, { useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { Button, Col, Row } from 'antd';
import {
  clearPortfolioProspecting,
  createPortfolioTaskAnalysis,
  createPortfolioTaskProspecting,
} from '../../../data/store/portfolio/portfolioActions';

import { ReactComponent as Pluse } from '../../../assets/cross-btn-icon.svg';
import { ReactComponent as Settings } from '../../../assets/settings-btn-icon.svg';
import { ReactComponent as Check } from '../../../assets/check-white-icon.svg';

import { ColorDataItem } from '../../ClientOverview/ClientPortfolioList/ClientPortfolioListItem';
import {
  numberWithSpaces,
  numberWithSpacesInter,
} from '../../../helpers/formatInteger';
import { LineBasic } from '../../../components/charts/LineBasic/LineBasic';
import OptimAssetsClass from '../OptimAssetsClass/OptimAssetsClass';
import { AreaRange } from '../../../components/charts/AreaRange/AreaRange';
import { BarColumnBasic } from '../../../components/charts/BarColumnBasic/BarColumnBasic';
import OptimChartHeader from '../OptimChartHeader/OptimChartHeader';

import ModalLeaveOpimisation from './ModalLeaveOpimisation/ModalLeaveOpimisation';
import Spinner from '../../Model/Spiner/Sriner';

import { getClassCategoryArray } from '../../../hooks/getClassCategoryArray';
import './style.scss';

const NewOptimizationStep = ({
  userId,
  portfolioId,
  settings,
  setPDFSource,
  optimalPortfolio,
  setOptimalPortfolio,
  optAlert,
  setOptAlert,
  setOptStart,
}) => {
  const { t } = useTranslation('');
  const dispatch = useDispatch();
  const history = useHistory();

  const {
    isLoading,
    analyzedPortfolio,
    analyzedPortfolioDot,
    prospectPortfolioSet,
  } = useSelector((state) => state.portfolioReducer);
  const currentPortfolio = useSelector(
    (state) => state.portfolioReducer.portfolioById,
  );
  const cashesList = useSelector(
    (state) => state.instrumentsReducer?.cashes?.list,
  );
  const { currencyList, assets } = useSelector(
    (state) => state.instrumentsReducer,
  );

  // eslint-disable-next-line no-unused-vars
  const [modalCancelVisible, setModalCancelVisible] = useState(false);
  const [tabs, setTabs] = useState(0);

  const handleTabs = (tab) => {
    setTabs(tab);
  };

  useEffect(() => {
    setOptStart(true);
    
    if (!settings) {
      history.push(
        `/accounts/${userId}/optimization/${portfolioId}/opt-settings`,
      );
    }
    const storName = sessionStorage.getItem('selectedPortfolio');
    if (storName && settings && analyzedPortfolio && prospectPortfolioSet) {
      setOptimalPortfolio(JSON.parse(storName));
    } else {
      sessionStorage.removeItem('selectedPortfolio');
    }
    return () => {
      setOptimalPortfolio(null);
      // dispatch(clearPortfolioProspecting());
    };
  }, []);

  useEffect(() => {
    setOptimalPortfolio(null);
    if (settings && Object.keys(settings)?.length) {
      const data = {
        ...settings,
        include_financial_plan: !!+settings?.include_financial_plan,
        monetary_basis: +settings?.monetary_basis,
      };
      setTimeout(() => {
        !isLoading &&
          dispatch(createPortfolioTaskProspecting({ portfolioId, data }));
      }, 100);
    }
  }, []);

  useEffect(() => {
    if (optAlert?.flag) setModalCancelVisible(true);
  }, [optAlert]);

  const getProspectPortfolioSet = useMemo(() => {
    return prospectPortfolioSet?.prospect_portfolios
      ?.map((i) => {
        return {
          name: i.id,
          y: i.mean_return,
          x: i.volatility,
          cvar: i.cvar,
          idiosync_risk: i.idiosync_risk,
        };
      })
      .sort((a, b) => a.x - b.x);
  }, [prospectPortfolioSet]);

  const chartAreaData = useMemo(() => {
    return getProspectPortfolioSet?.map((i, index) => ({
      ...i,
      key: index + 1,
    }));
  }, [getProspectPortfolioSet]);

  const choosePortfolio = (data) => {
    sessionStorage.setItem('selectedPortfolio', JSON.stringify(data.options));
    setOptimalPortfolio(data.options);
  };

  useEffect(() => {
    if (
      optimalPortfolio &&
      settings &&
      analyzedPortfolio &&
      prospectPortfolioSet
    ) {
      dispatch(
        createPortfolioTaskAnalysis({
          portfolioId,
          include_financial_plan: !!+settings?.include_financial_plan,
          name: optimalPortfolio.name,
          fees: {
            ...settings.fee_flags,
            withdrawal_amount: settings.withdrawal_amount,
            deposit_amount: settings.deposit_amount,
            reserved_amount: settings.reserved_amount,
            monetary_basis: settings?.monetary_basis,
            start_value: analyzedPortfolio?.start_value,
          },
        }),
      );
    }
  }, [optimalPortfolio]);

  const portfolioTotalValueOpt = useMemo(() => {
    if (prospectPortfolioSet && optimalPortfolio?.name) {
      return prospectPortfolioSet?.prospect_portfolios
        ?.find((i) => i.id === optimalPortfolio?.name)
        ?.items.reduce((acc, el) => acc + el.value, 0);
    } else {
      return 0;
    }
  }, [prospectPortfolioSet, optimalPortfolio]);

  const optimalData = useMemo(
    () => ({
      portfolio_content: prospectPortfolioSet?.prospect_portfolios?.find(
        (i) => i.id === optimalPortfolio?.name,
      )?.items,
      unlisted_content: [],
      portfolio_cashes: [],
      total_value: portfolioTotalValueOpt,
    }),
    [prospectPortfolioSet, optimalPortfolio],
  );

  const handleReset = () => {
    setOptAlert({ flag: false, path: '' });
    setOptStart(false);
    history.push(`/accounts/${userId}/portfolio`, 'edit');
  };

  const getChartData = (type) => {
    if (type === 'data1') {
      return (
        analyzedPortfolio?.projection?.map(
          (i) => [i?.year, i?.value_sek] ?? null,
        ) ?? []
      );
    }
    if (type === 'data2') {
      return (
        analyzedPortfolioDot?.projection?.map((i) => [i?.year, i?.value_sek]) ??
        []
      );
    }
    if (type === 'data3') {
      return analyzedPortfolio?.risk?.map((i) => [i?.year, i?.value_sek]) ?? [];
    }
    if (type === 'data4') {
      return (
        analyzedPortfolioDot?.risk?.map((i) => [i?.year, i?.value_sek]) ?? []
      );
    }
    if (type === 'data5') {
      return (
        analyzedPortfolio?.wealth_appreciation?.map((i) => [
          i?.year,
          i?.value,
          {
            return_amount: i?.return_amount,
            deposit_fee_amount: i?.deposit_fee_amount,
            product_fee_amount: i?.product_fee_amount,
            advisory_fee_amount: i?.advisory_fee_amount,
            deposit_amount: i?.deposit_amount,
            withdrawal_amount: i?.withdrawal_amount,
            tax_amount: i?.tax_amount,
          },
        ]) ?? []
      );
    }
    if (type === 'data6') {
      return (
        analyzedPortfolioDot?.wealth_appreciation?.map((i) => [
          i?.year,
          i?.value,
          {
            return_amount: i?.return_amount,
            deposit_fee_amount: i?.deposit_fee_amount,
            product_fee_amount: i?.product_fee_amount,
            advisory_fee_amount: i?.advisory_fee_amount,
            deposit_amount: i?.deposit_amount,
            withdrawal_amount: i?.withdrawal_amount,
            tax_amount: i?.tax_amount,
          },
        ]) ?? []
      );
    }
  };

  const handleBack = () => {
    setOptStart(false);
    sessionStorage.removeItem('selectedPortfolio');
    dispatch(clearPortfolioProspecting());
    setPDFSource([]);
    history.goBack();
  };

  const handleNext = () => {
    const template = captureForPDF();
    const data = {
      page1_graph: template?.partOneOne,
      page1_cards: template?.partOneTwo,
      page2_graph: template?.partTwo,
      page3_graph:
        settings?.include_financial_plan === '1' ? template?.partThree : null,
      page4_asset_class: template?.partFour,
      page5_category: template?.partFive,
    };
    sessionStorage.setItem('report_data', JSON.stringify(data));
    setPDFSource(data);
    setTimeout(() => {
      history.push(
        `/accounts/${userId}/optimization/${portfolioId}/transactions`,
      );
    }, 500);
  };

  const captureForPDF = () => {
    const { mean_return, volatility, cvar } = analyzedPortfolio;
    const { x, y, cvar: z } = optimalPortfolio;
    const partOneOne = {
      type: 'lineBasic',
      data: chartAreaData,
      currentPortfolioData: [
        {
          key: 'current',
          y: Math.round(mean_return * 10) / 10,
          x: Math.round(volatility * 10) / 10,
          cvar: Math.round(cvar * 10) / 10,
        },
      ],
      selectedItem: optimalPortfolio,
    };
    const partOneTwo = {
      optim_data: {
        mean_return: numberWithSpacesInter(y, 2),
        volatility: numberWithSpacesInter(x, 2),
        cvar: numberWithSpacesInter(z, 2),
      },
      current_data: {
        mean_return: numberWithSpacesInter(mean_return, 2),
        volatility: numberWithSpacesInter(volatility, 2),
        cvar: numberWithSpacesInter(cvar, 2),
      },
    };
    const partTwo = {
      type: 'areaRange',
      data: [
        getChartData('data1'),
        getChartData('data2'),
        getChartData('data3'),
        getChartData('data4'),
      ],
      currentPortfolioData: null,
      selectedItem: null,
    };
    const partThree = {
      type: 'barColumnBasic',
      data: [getChartData('data5'), getChartData('data6')],
      currentPortfolioData: null,
      selectedItem: null,
    };
    const assetCategoryCurrent = getClassCategoryArray(
      [currentPortfolio],
      cashesList,
      assets,
      true,
    );
    const classData = (type) =>
      assetCategoryCurrent?.[type]?.map((i) => {
        return {
          name: i.name,
          value: numberWithSpacesInter(i.value, 0),
          value_persent: Math.round(i.value_persent * 100) / 100,
          weight: numberWithSpacesInter(i.weight, 2),
        };
      });
    const partFour = {
      current_class: classData('class'),
      optimized_class: [],
    };
    const partFive = {
      current_category: classData('category'),
      optimized_category: [],
    };

    return {
      partOneOne,
      partOneTwo,
      partTwo,
      partThree,
      partFour,
      partFive,
    };
  };

  const closeModal = () => {
    setModalCancelVisible(false);
  };

  return (
    <div className="optimization-component-wrapper charts-page">
      <div className="top-btn-wrapper">
        <Button
          type="text"
          className="portfolio-cancel-btn"
          icon={<Pluse />}
          iconPosition="end"
          onClick={handleReset}
        >
          {t('CANCEL')}
        </Button>
        <Button
          icon={<Settings />}
          iconPosition="end"
          className="optim-settings-btn"
          onClick={handleBack}
        >
          {t('SETTINGS')}
        </Button>
        <Button
          icon={<Check />}
          iconPosition="end"
          disabled={!optimalPortfolio?.name || isLoading}
          onClick={handleNext}
          className="optim-continue-btn"
        >
          {t('TRANSACTIONS')}
        </Button>
      </div>
      <section className="prospects-sections-wrapper">
        <div className="current-portfolio-info portfolio-card">
          <div>
            <p className="portfolio-card-name">
              {currentPortfolio?.name}{' '}
              <span className="portfolio-card-code">
                {currentPortfolio?.portfolio_code}
              </span>
            </p>
          </div>
          <div>
            <p className="portfolio-card-value">{`${numberWithSpaces(Math.round(currentPortfolio?.total_value))} kr`}</p>
            <Row justify={'end'} className="portfolio-card-text">
              <Col className="">
                <ColorDataItem
                  isSelected={true}
                  text={t('RETURN')}
                  value={currentPortfolio?.return}
                />
              </Col>
              <Col className="">
                <ColorDataItem
                  isSelected={true}
                  text={t('VOLATILITY')}
                  value={currentPortfolio?.vol}
                />
              </Col>
              <Col className="">
                <ColorDataItem
                  isSelected={true}
                  text={t('CVaR')}
                  value={currentPortfolio?.cvar}
                />
              </Col>
            </Row>
          </div>
        </div>
        <div className="prospect-line-chart">
          {isLoading && !prospectPortfolioSet ? (
            <Spinner position="absolute" width="100%" height="100%" />
          ) : (
            <LineBasic
              data={getProspectPortfolioSet}
              currentPortfolioData={[
                {
                  key: 'current',
                  y: Math.round(analyzedPortfolio?.mean_return * 10) / 10,
                  x: Math.round(analyzedPortfolio?.volatility * 10) / 10,
                  cvar: Math.round(analyzedPortfolio?.cvar * 10) / 10,
                  idiosync_risk: analyzedPortfolio?.idiosync_risk,
                },
              ]}
              selectPoint={(data) => choosePortfolio(data)}
              point={optimalPortfolio?.name}
              analyzedPortfolio={!!analyzedPortfolio}
            />
          )}
        </div>
      </section>
      <section className="compare-sections-wrapper">
        <div className="charts-tabs-block">
          <div className="">
            <Row>
              <Col>
                <Button
                  type="text"
                  className={`portfolio-tabs-btn ${tabs === 0 ? 'selected' : ''}`}
                  onClick={() => handleTabs(0)}
                >
                  {t('PORTFOLIO_DEVELOPMENT')}
                </Button>
                {settings?.include_financial_plan === '1' && (
                  <Button
                    type="text"
                    className={`portfolio-tabs-btn ${tabs === 1 ? 'selected' : ''}`}
                    onClick={() => handleTabs(1)}
                  >
                    {t('WEALTH_APPRECIATION')}
                  </Button>
                )}
              </Col>
            </Row>
          </div>
          <div style={{ minHeight: '300px', position: 'relative' }}>
            {isLoading ? (
              <Spinner position="absolute" width="100%" height="100%" />
            ) : (
              <>
                {tabs === 0 && optimalPortfolio && (
                  <div className="area-range-chart-wrapper">
                    <AreaRange
                      data1={getChartData('data1')}
                      data2={getChartData('data2')}
                      data3={getChartData('data3')}
                      data4={getChartData('data4')}
                    />
                    <p className="area-range-chart-footer">
                      {t('AREA_CHART_FOOTER')}
                    </p>
                  </div>
                )}
                {tabs === 1 && optimalPortfolio && (
                  <BarColumnBasic
                    data1={getChartData('data5')}
                    data2={getChartData('data6')}
                  />
                )}
              </>
            )}
          </div>
          <OptimChartHeader
            current={currentPortfolio}
            optimal={optimalPortfolio}
          />
        </div>
        <div className="class-category-block">
          <OptimAssetsClass
            cashesList={cashesList}
            currencyList={currencyList}
            currentData={currentPortfolio}
            optimalData={optimalData}
          />
        </div>
      </section>
      <ModalLeaveOpimisation
        t={t}
        modalCancelVisible={modalCancelVisible}
        closeModal={closeModal}
        optAlert={optAlert}
        setOptAlert={setOptAlert}
        setOptStart={setOptStart}
      />
    </div>
  );
};

export default NewOptimizationStep;

NewOptimizationStep.propTypes = {
  userId: PropTypes.string,
  portfolioId: PropTypes.string,
  setPDFSource: PropTypes.func,
  settings: PropTypes.object,
  optimalPortfolio: PropTypes.object,
  setOptimalPortfolio: PropTypes.func,
  optAlert: PropTypes.object,
  setOptAlert: PropTypes.func,
  setOptStart: PropTypes.func,
};
